import { gql } from "@apollo/client";
import { ApiService } from "./ApiService";
import { OpResult } from "@sdflc/api-helpers";
import { Pagination, SortItem } from "./commonApiTypes";
import { User } from "./UserService";
import { Event } from "./EventService";
import { Room } from "./RoomService";

// GraphQL queries
const EVENT_ROOM_ASSIGNMENT_LIST_QUERY = gql`
  query EventRoomAssignmentList(
    $filter: EventRoomAssignmentFilter
    $params: PaginationAndSorting
  ) {
    eventRoomAssignmentList(filter: $filter, params: $params) {
      code
      data {
        id
        eventId
        roomId
        assignmentType
        notes
        createdAt
        createdBy
        userCreated {
          id
        }
        event {
          id
        }
        room {
          id
        }
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const EVENT_ROOM_ASSIGNMENT_GET_QUERY = gql`
  query EventRoomAssignmentGet($id: ID) {
    eventRoomAssignmentGet(id: $id) {
      code
      data {
        id
        eventId
        roomId
        assignmentType
        notes
        createdAt
        createdBy
        userCreated {
          id
        }
        event {
          id
        }
        room {
          id
        }
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const EVENT_ROOM_ASSIGNMENT_GET_MANY_QUERY = gql`
  query EventRoomAssignmentGetMany($ids: [ID]) {
    eventRoomAssignmentGetMany(ids: $ids) {
      code
      data {
        id
        eventId
        roomId
        assignmentType
        notes
        createdAt
        createdBy
        userCreated {
          id
        }
        event {
          id
        }
        room {
          id
        }
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

// GraphQL mutations
const EVENT_ROOM_ASSIGNMENT_CREATE_MUTATION = gql`
  mutation EventRoomAssignmentCreate($params: EventRoomAssignmentInput) {
    eventRoomAssignmentCreate(params: $params) {
      code
      data {
        id
        eventId
        roomId
        assignmentType
        notes
        createdAt
        createdBy
        userCreated {
          id
        }
        event {
          id
        }
        room {
          id
        }
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const EVENT_ROOM_ASSIGNMENT_UPDATE_MUTATION = gql`
  mutation EventRoomAssignmentUpdate(
    $where: EventRoomAssignmentWhereInput
    $params: EventRoomAssignmentInput
  ) {
    eventRoomAssignmentUpdate(where: $where, params: $params) {
      code
      data {
        id
        eventId
        roomId
        assignmentType
        notes
        createdAt
        createdBy
        userCreated {
          id
        }
        event {
          id
        }
        room {
          id
        }
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const EVENT_ROOM_ASSIGNMENT_REMOVE_MUTATION = gql`
  mutation EventRoomAssignmentRemove($where: EventRoomAssignmentWhereInput) {
    eventRoomAssignmentRemove(where: $where) {
      code
      data {
        id
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const EVENT_ROOM_ASSIGNMENT_REMOVE_MANY_MUTATION = gql`
  mutation EventRoomAssignmentRemoveMany(
    $where: [EventRoomAssignmentWhereInput]
  ) {
    eventRoomAssignmentRemoveMany(where: $where) {
      code
      data {
        id
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

export interface EventRoomAssignment {
  id: string;
  eventId?: string;
  roomId?: string;
  assignmentType?: string;
  notes?: string;
  createdAt?: string;
  createdBy?: string;
  userCreated?: User;
  event?: Event;
  room?: Room;
}

export interface EventRoomAssignmentFilter {
  id?: string[];
  eventId?: string[];
  roomId?: string[];
  assignmentType?: string[];
}

export interface EventRoomAssignmentInput {
  eventId?: string;
  roomId?: string;
  assignmentType?: string;
  notes?: string;
}

export interface EventRoomAssignmentWhereInput {
  id: string;
}

/**
 * Event Room Assignment service that handles all event room assignment-related API calls
 * Supports full CRUD operations: list, get, getMany, create, update, remove, removeMany
 */
export class EventRoomAssignmentService {
  /**
   * Get a list of event room assignments with optional filtering, pagination, and sorting
   */
  static async list(
    filter?: EventRoomAssignmentFilter,
    pagination?: Pagination,
    sortBy?: SortItem[]
  ): Promise<OpResult> {
    const result = await ApiService.executeQuery(
      EVENT_ROOM_ASSIGNMENT_LIST_QUERY,
      {
        filter: filter || {},
        params: {
          pagination: pagination || undefined,
          sortBy: sortBy || undefined,
        },
      }
    );
    return result;
  }

  /**
   * Get a single event room assignment by ID
   */
  static async get(id: string): Promise<OpResult> {
    const result = await ApiService.executeQuery(
      EVENT_ROOM_ASSIGNMENT_GET_QUERY,
      {
        id: id,
      }
    );
    return result;
  }

  /**
   * Get multiple event room assignments by IDs (preserves order)
   */
  static async getMany(ids: string[]): Promise<OpResult> {
    const result = await ApiService.executeQuery(
      EVENT_ROOM_ASSIGNMENT_GET_MANY_QUERY,
      {
        ids: ids,
      }
    );
    return result;
  }

  /**
   * Create a new event room assignment
   */
  static async create(params: EventRoomAssignmentInput): Promise<OpResult> {
    const result = await ApiService.executeMutation(
      EVENT_ROOM_ASSIGNMENT_CREATE_MUTATION,
      {
        params,
      }
    );
    return result;
  }

  /**
   * Update an existing event room assignment
   */
  static async update(
    id: string,
    params: EventRoomAssignmentInput
  ): Promise<OpResult> {
    const result = await ApiService.executeMutation(
      EVENT_ROOM_ASSIGNMENT_UPDATE_MUTATION,
      {
        where: { id },
        params,
      }
    );
    return result;
  }

  /**
   * Remove/delete an event room assignment
   */
  static async remove(id: string): Promise<OpResult> {
    const result = await ApiService.executeMutation(
      EVENT_ROOM_ASSIGNMENT_REMOVE_MUTATION,
      {
        where: { id },
      }
    );
    return result;
  }

  /**
   * Remove/delete multiple event room assignments
   */
  static async removeMany(ids: string[]): Promise<OpResult> {
    const where = ids.map((id) => ({ id }));
    const result = await ApiService.executeMutation(
      EVENT_ROOM_ASSIGNMENT_REMOVE_MANY_MUTATION,
      {
        where,
      }
    );
    return result;
  }
}
